import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

function KpiCard({ label, value }) {
  return (
    <div
      className="bg-white rounded-xl px-5 py-4"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
    >
      <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide">{label}</p>
      <p className="text-3xl font-semibold text-[#171717] mt-1" style={{ letterSpacing: '-2.4px' }}>
        {value === undefined || value === null ? (
          <span className="text-lg text-[#ebebeb]">—</span>
        ) : (
          value.toLocaleString()
        )}
      </p>
    </div>
  );
}

function PlanBadge({ plan }) {
  return (
    <span
      className="text-xs font-medium px-2.5 py-0.5 rounded-full capitalize text-[#4d4d4d]"
      style={{
        boxShadow: plan === 'pro' || plan === 'enterprise'
          ? 'rgba(0,0,0,0.12) 0px 0px 0px 1px'
          : 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
        background: plan === 'pro' || plan === 'enterprise' ? '#171717' : '#fafafa',
        color: plan === 'pro' || plan === 'enterprise' ? '#fff' : '#4d4d4d',
      }}
    >
      {plan}
    </span>
  );
}

function PmfSignalRow({ signal }) {
  const isComplex = signal.id === 4;
  const pct = typeof signal.value === 'number' ? signal.value : null;
  const target = signal.target;
  const barWidth = target && pct !== null ? Math.min(100, Math.round((pct / target) * 100)) : 0;

  return (
    <div className="py-4" style={{ borderBottom: '1px solid #ebebeb' }}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold flex-shrink-0"
              style={{
                background: signal.met ? '#171717' : '#ebebeb',
                color: signal.met ? '#fff' : '#808080',
              }}
            >
              {signal.met ? '✓' : signal.id}
            </span>
            <span className="text-sm font-medium text-[#171717]">{signal.name}</span>
          </div>
          {!isComplex && pct !== null && target && (
            <div className="ml-7">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 rounded-full h-1.5 overflow-hidden" style={{ background: '#ebebeb' }}>
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${barWidth}%`, background: signal.met ? '#171717' : '#0a72ef' }}
                  />
                </div>
                <span className="text-xs text-[#808080] flex-shrink-0">
                  {pct}% / {target}%
                  {signal.cohort !== undefined && signal.cohort !== null && (
                    <span className="ml-1 text-[#ebebeb]">· n={signal.cohort}</span>
                  )}
                </span>
              </div>
              {signal.breakdown && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(signal.breakdown).map(([k, v]) => (
                    <span key={k} className="text-xs text-[#808080]">
                      {k}: <span className="font-medium text-[#4d4d4d]">{v}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {isComplex && typeof signal.value === 'object' && signal.value && (
            <div className="ml-7 flex gap-4 mt-0.5">
              <span className="text-xs text-[#808080]">
                Active Pro: <span className="font-semibold text-[#171717]">{signal.value.active_pro}</span>
                <span className="text-[#ebebeb]"> / 75</span>
              </span>
              <span className="text-xs text-[#808080]">
                Enterprise: <span className="font-semibold text-[#171717]">{signal.value.enterprise}</span>
                <span className="text-[#ebebeb]"> / 3</span>
              </span>
            </div>
          )}
          {signal.id === 5 && signal.value === 0 && (
            <p className="ml-7 text-xs text-[#808080] mt-0.5">No non-life-science signups yet.</p>
          )}
          {(pct === null && !isComplex && signal.id !== 5) && (
            <p className="ml-7 text-xs text-[#808080] mt-0.5">Not enough data yet.</p>
          )}
        </div>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5"
          style={{
            background: signal.met ? '#171717' : '#fafafa',
            color: signal.met ? '#fff' : '#808080',
            boxShadow: signal.met ? 'none' : 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
          }}
        >
          {signal.met ? 'MET' : 'NOT YET'}
        </span>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [pmf, setPmf] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || user.plan !== 'enterprise') return;
    Promise.all([
      api.get('/api/admin/stats'),
      api.get('/api/admin/pmf'),
    ])
      .then(([statsRes, pmfRes]) => {
        setStats(statsRes.data);
        setPmf(pmfRes.data);
      })
      .catch(() => setError('Failed to load admin stats.'))
      .finally(() => setFetching(false));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#808080]">
        Loading...
      </div>
    );
  }

  if (!user || user.plan !== 'enterprise') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Admin Dashboard</h1>
            <p className="text-xs text-[#808080] mt-0.5">Platform analytics and user management</p>
          </div>
          <Link to="/" className="text-sm text-[#4d4d4d] hover:text-[#171717] font-medium transition-colors">
            Back to App
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {fetching ? (
          <div className="flex items-center justify-center py-24 text-[#808080]">Loading stats...</div>
        ) : stats ? (
          <div className="space-y-8">
            {/* PMF Signals */}
            {pmf && (
              <section
                className="bg-white rounded-xl overflow-hidden"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #ebebeb' }}>
                  <div>
                    <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide">PMF Signals</h2>
                    <p className="text-xs text-[#808080] mt-0.5">Need 4/5 to unlock expansion</p>
                  </div>
                  <div className="text-right">
                    <span
                      className="text-2xl font-semibold"
                      style={{ letterSpacing: '-1.28px', color: pmf.expansion_ready ? '#171717' : '#808080' }}
                    >
                      {pmf.signals_met}/{pmf.signals_needed}
                    </span>
                    {pmf.expansion_ready && (
                      <p className="text-xs font-semibold text-[#171717] mt-0.5">EXPANSION READY</p>
                    )}
                  </div>
                </div>
                <div className="px-6 divide-y-0">
                  {pmf.signals.map((signal) => (
                    <PmfSignalRow key={signal.id} signal={signal} />
                  ))}
                </div>
              </section>
            )}

            {/* KPI Cards */}
            <section>
              <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-3">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Total Users" value={stats.total_users} />
                <KpiCard label="Pro Users" value={stats.users_by_plan?.pro} />
                <KpiCard label="Enterprise Users" value={stats.users_by_plan?.enterprise} />
                <KpiCard label="Total Searches" value={stats.total_searches} />
              </div>
            </section>

            {/* Searches Today */}
            <section
              className="bg-white rounded-xl px-6 py-5"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1">Searches Today</h2>
              <p className="text-4xl font-semibold text-[#171717]" style={{ letterSpacing: '-2.4px' }}>
                {stats.searches_today?.toLocaleString() ?? '—'}
              </p>
            </section>

            {/* Users by Plan */}
            <section
              className="bg-white rounded-xl px-6 py-5"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-4">Users by Plan</h2>
              {(() => {
                const planData = [
                  { label: 'Free', value: stats.users_by_plan?.free ?? stats.total_users - (stats.users_by_plan?.pro || 0) - (stats.users_by_plan?.enterprise || 0) },
                  { label: 'Pro', value: stats.users_by_plan?.pro },
                  { label: 'Enterprise', value: stats.users_by_plan?.enterprise },
                ];
                const total = stats.total_users || 1;
                return (
                  <div className="space-y-3">
                    {planData.map((p) => (
                      <div key={p.label} className="flex items-center gap-3">
                        <span className="w-20 text-xs text-[#4d4d4d] font-medium">{p.label}</span>
                        <div className="flex-1 rounded-full h-2 overflow-hidden" style={{ background: '#ebebeb' }}>
                          <div
                            className="h-2 rounded-full bg-[#171717] transition-all duration-500"
                            style={{ width: `${Math.round(((p.value || 0) / total) * 100)}%` }}
                          />
                        </div>
                        <span className="w-8 text-xs text-[#808080] text-right">{p.value ?? 0}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </section>

            {/* Recent Signups */}
            <section
              className="bg-white rounded-xl overflow-hidden"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              <div className="px-6 py-4" style={{ borderBottom: '1px solid #ebebeb' }}>
                <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide">Recent Signups</h2>
              </div>
              {stats.recent_signups && stats.recent_signups.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th className="px-6 py-3 text-left text-xs text-[#808080] font-semibold uppercase tracking-wide">Email</th>
                      <th className="px-6 py-3 text-left text-xs text-[#808080] font-semibold uppercase tracking-wide">Plan</th>
                      <th className="px-6 py-3 text-left text-xs text-[#808080] font-semibold uppercase tracking-wide">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_signups.map((u, i) => (
                      <tr key={i} className="hover:bg-[#fafafa] transition-colors" style={{ borderTop: '1px solid #ebebeb' }}>
                        <td className="px-6 py-3 text-[#171717]">{u.email}</td>
                        <td className="px-6 py-3">
                          <PlanBadge plan={u.plan} />
                        </td>
                        <td className="px-6 py-3 text-[#808080]">
                          {u.joined_date
                            ? new Date(u.joined_date).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="px-6 py-8 text-sm text-[#808080] text-center">No recent signups.</p>
              )}
            </section>

            {/* Top Search Queries */}
            <section
              className="bg-white rounded-xl overflow-hidden"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              <div className="px-6 py-4" style={{ borderBottom: '1px solid #ebebeb' }}>
                <h2 className="text-xs font-semibold text-[#808080] uppercase tracking-wide">Top Search Queries</h2>
              </div>
              {stats.top_queries && stats.top_queries.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th className="px-6 py-3 text-left text-xs text-[#808080] font-semibold uppercase tracking-wide">Query</th>
                      <th className="px-6 py-3 text-right text-xs text-[#808080] font-semibold uppercase tracking-wide">Searches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_queries.map((q, i) => (
                      <tr key={i} className="hover:bg-[#fafafa] transition-colors" style={{ borderTop: '1px solid #ebebeb' }}>
                        <td className="px-6 py-3 text-[#4d4d4d]">{q.query}</td>
                        <td className="px-6 py-3 text-right text-[#171717] font-semibold">
                          {q.count?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="px-6 py-8 text-sm text-[#808080] text-center">No query data available.</p>
              )}
            </section>
          </div>
        ) : null}
      </main>
    </div>
  );
}
